import { forwardRef, useState, type HTMLAttributes } from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'
import { cn } from '@/lib/utils'

export const Avatar = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
    {...props}
  />
))
Avatar.displayName = AvatarPrimitive.Root.displayName

export const AvatarImage = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
))
AvatarImage.displayName = AvatarPrimitive.Image.displayName

export const AvatarFallback = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'bg-sunk text-ink flex h-full w-full items-center justify-center text-sm font-medium',
      className,
    )}
    {...props}
  />
))
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName

/* ----------------------------------------------------------------------------
 * AvatarGradient — deterministic gradient avatar for users without an image.
 *
 * Per Story 1.10 (AC10-13):
 *   • Gradient selection is PURE: `initials.charCodeAt(0) % GRADIENTS.length`.
 *   • Two instances with the same initials always render the same gradient.
 *   • Default size 30px (matches BrandMark scale used in the sidebar).
 *   • Font-size = round(size * 0.38).
 *   • Inset highlight shadow: `inset 0 1px 0 oklch(1 0 0 / 0.2)`.
 *
 * Gradients are theme-agnostic (fixed oklch values) so they look consistent
 * across light and dark mode (AC26). Lumen migrated them from hex to oklch,
 * keeping five clearly distinct hues.
 * -------------------------------------------------------------------------- */

export const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, oklch(0.84 0.13 133), oklch(0.72 0.15 141))', // verde
  'linear-gradient(135deg, oklch(0.80 0.11 235), oklch(0.66 0.13 245))', // azul
  'linear-gradient(135deg, oklch(0.82 0.12 62), oklch(0.68 0.15 32))', // âmbar → coral
  'linear-gradient(135deg, oklch(0.78 0.13 300), oklch(0.62 0.16 292))', // violeta
  'linear-gradient(135deg, oklch(0.84 0.12 108), oklch(0.68 0.13 118))', // oliva
] as const

export interface AvatarGradientProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /** Display initials (typically 1-2 chars). First char drives the gradient. */
  initials: string
  /** Width + height in pixels. Defaults to 30 to match BrandMark. */
  size?: number
  /**
   * Story 15.2 — URL da foto real. Opcional de propósito: os call sites que
   * desenham PROFISSIONAIS (listagens, dashboard) continuam a passar só
   * `initials` e nada muda para eles.
   *
   * O gradiente NÃO é um placeholder que a foto substitui e pronto — é o
   * fallback vivo. Sem `src`, e também quando o `<img>` falha a carregar
   * (`onError`), volta-se ao gradiente. Um `<img>` partido deixaria um buraco
   * no rodapé da sidebar, que é onde este componente é mais visível.
   */
  src?: string | null
  /**
   * Story 31.8 — `lazy` numa GRELHA de fotos (os cartões da lista de profissionais):
   * uma página de 100 cartões pedia as fotos todas de uma vez, e cada uma é uma ida ao
   * Worker. Por omissão o browser decide, como sempre.
   */
  loading?: 'lazy' | 'eager'
}

/**
 * Compute the gradient index for a given initials string.
 * Exported for snapshot testing (AC10/AC11) and to prove determinism.
 */
export function getGradientIndex(initials: string): number {
  if (!initials || initials.length === 0) return 0
  return initials.charCodeAt(0) % AVATAR_GRADIENTS.length
}

export const AvatarGradient = forwardRef<HTMLSpanElement, AvatarGradientProps>(
  ({ initials, size = 30, src, loading, className, style, ...props }, ref) => {
    const idx = getGradientIndex(initials)
    const gradient = AVATAR_GRADIENTS[idx]
    const fontSize = Math.round(size * 0.38)

    // Guarda-se o `src` que FALHOU, não um booleano: quando a foto muda, o URL
    // muda com ela (o cache-buster `?v=` de `meAvatarUrl`), e a comparação
    // rearma o `<img>` sozinha. Um booleano ficaria preso em `true` e a foto
    // nova nunca chegaria a ser tentada.
    const [failedSrc, setFailedSrc] = useState<string | null>(null)
    const showImage = Boolean(src) && src !== failedSrc

    return (
      <span
        ref={ref}
        aria-label={`Avatar ${initials}`}
        className={cn(
          'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
          // `text-avatar-fg` e não `text-white`: medido, branco sobre estes
          // gradientes dava 1,57:1 no pior caso e não passava 3:1 em nenhum dos
          // dez pontos. A tinta escura mede 4,86:1 no pior caso. Ver a nota do
          // token em globals.css §1, que também explica o L escolhido.
          'text-avatar-fg font-semibold',
          'shadow-[inset_0_1px_0_oklch(1_0_0_/_0.2)]',
          className,
        )}
        style={{
          width: size,
          height: size,
          fontSize,
          background: gradient,
          ...style,
        }}
        {...props}
      >
        {showImage ? (
          // `alt=""` + `aria-hidden`: o nome acessível já está no `aria-label`
          // do `<span>`, e uma imagem com alt próprio fá-lo-ia ler duas vezes.
          // O gradiente fica por baixo — é ele que se vê enquanto os bytes não
          // chegam, e é ele que reaparece se o `onError` disparar.
          <img
            src={src ?? undefined}
            {...(loading ? { loading } : {})}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
            onError={() => setFailedSrc(src ?? null)}
          />
        ) : (
          initials
        )}
      </span>
    )
  },
)
AvatarGradient.displayName = 'AvatarGradient'
